import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { tap, catchError, takeUntil } from 'rxjs/operators';
import { Appointment } from '../appointment-details/models/appointment.model';
import { ConfirmDialogService } from '../confirm-dialog/services/confirm-dialog.service';
import { AuthService } from '../core/auth-service';
import { VehicleDetails } from '../models/vehicle-details';
import { AnswerValue } from '../purchase/models/answer-value.model';
import { NotificationService } from '../services/notification.service';
import { PurchaseService } from '../services/purchase.service';
import { VehicleCheck } from '../vehicle-check-hpi/models/vehicle-check.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthTask } from '../task-list/models/auth-task.model';
import { AuthorizationRequestService } from '../services/authorization-request.service';
import { ImageService } from '../services/image.service';
import { ImageTypes } from '../enums/image-types';
import { BaseComponentDirective } from '../base/base.component';
import { Router } from '@angular/router';
import { DisplayAnswerComponent } from './display-answer/display-answer.component';
import { AuthorizationTypes } from '../authorization/enums/authorization-types';
import { FlexAuth } from './models/flex-auth.model';
import { PathFinderService } from '../services/path-finder.service';
import { CapHpiComparison } from '../walk-in/models/cap-hpi-comparison.model';
import { DeclineReasonComponent } from './decline-reason/decline-reason.component';
import { EventsService } from '../services/events.service';
import { MechGradeOverrideComponent } from './mech-grade-override/mech-grade-override.component';
import { MechGradeAuth } from './models/mech-grade-auth.model';

@Component({
  selector: 'app-appointment-details-auth',
  templateUrl: './appointment-details-auth.component.html',
  styleUrls: ['./appointment-details-auth.component.scss']
})
export class AppointmentDetailsAuthComponent extends BaseComponentDirective implements OnInit {
  constructor(
    private modal: NgbActiveModal,
    private purchaseService: PurchaseService,
    private notifications: NotificationService,
    private router: Router,
    private authService: AuthService,
    private modalService: NgbModal,
    private confirmService: ConfirmDialogService,
    private authRequestService: AuthorizationRequestService,
    private imageService: ImageService,
    private sanitizer: DomSanitizer,
    private pathFinder: PathFinderService,
    private eventsService: EventsService) { super(); }

  @Input() vehicleDetails: VehicleDetails;
  @Input() mileage: number;
  @Input() appointmentId: number;
  @Input() buyerExpLevel: number;
  @Input() authTask: AuthTask;
  @Input() differentBuyer: boolean;
  @Input() buyerName: string;

  loading: boolean;

  username: string;
  stolen: string;
  finance: string;
  collision: string;
  rkc: string;
  previewString: SafeUrl;
  previewStamp: string;
  quoteStateId: number;
  mileageDecision: string;
  vehicleDetailsCollapsed = true;
  vehicleConditionCollapsed = true;
  mileageDetailsCollapsed = true;
  documentsCollapsed = true;
  vehicleImagesCollapsed = true;
  hasAuthDetailsTab = false;
  activeTab = 0;
  ancestryCategory: string;
  allAnswers: Array<AnswerValue>;
  optionalExtras = new Array<AnswerValue>();
  vehicleCheck: VehicleCheck = null;
  modalData$: Observable<Appointment>;
  appointment: Appointment;
  ApproveText = 'Are you sure you want to\napprove this authorisation?';
  DeclineText = 'Are you sure you want to\ndecline this authorisation?';
  defaultVehicleImage = '../../assets/images/unknownCar.png';
  hpiVehicleDetails: VehicleDetails;
  flexAuth: FlexAuth;
  suggestedWriteOffDesc: string;
  chosenWriteOffDesc: string;
  HPIFirstRegistered: string;
  vrm: string;
  changeVrm: string;
  altVrm: string;
  vrmHpiDetails: string;
  capHpiComparison: CapHpiComparison;
  declineSubscription: Subscription;
  DVSAMOTDate: string;
  buyerMechGrade: string;
  actualMechGrade: string;

  ngOnInit(): void {
    this.loading = true;
    this.setHasAuthDetailsTab();
    this.appointment = new Appointment();
    this.username = this.authService.getUser()?.name;
    this.modalData$ = this.purchaseService
      .getAuthTaskAppointmentDetail$(this.appointmentId, this.authTask.authorizationTypeId)
      .pipe(
        tap((result) => {
          this.allAnswers = result.answers;
          this.capHpiComparison = result.capHpiComparison;
          this.DVSAMOTDate = result.dvsamotDate;
          const isBookedVrm = result.answers.find(item => item.parameterName === 'IsBookedVrm');
          if (isBookedVrm && isBookedVrm.value === 'true') {
            this.vrm = result.vrm;
            this.altVrm = result.vrm;
            this.vrmHpiDetails = result.altVrm;
          } else {
            this.vrm = result.vrm;
            this.altVrm = result.altVrm;
            this.vrmHpiDetails = result.altVrm;
          }
          this.optionalExtras = this.allAnswers.filter(x => x.isOptionalExtra === true);
          this.quoteStateId = result.quoteStateId;
          result.documents.forEach(doc => {
            if (doc.imgAsBase64String) {
              doc.imgAsBase64String = this.sanitizer.bypassSecurityTrustUrl(doc.imgAsBase64String.toString());
            }
          });

          result.vehicleImages.forEach(vi => {
            if (vi.imgAsBase64String) {
              vi.imgAsBase64String = this.sanitizer.bypassSecurityTrustUrl(vi.imgAsBase64String.toString());
            }
          });

          if ((result.source === 'WBAC' || result.source === 'WBAV') || result.authorizationRequests.find(x => x.authorizationTypeId === AuthorizationTypes.VehicleDetailsChanged)) {
            this.vehicleDetails = new VehicleDetails(
              result.valuation.manufacturer,
              result.valuation.colour,
              result.valuation.model,
              result.valuation.derivative,
              result.valuation.engineSize,
              result.valuation.bookId,
              result.valuation.capId,
              result.valuation.imageUrl,
              result.valuation.fuelType,
              result.valuation.hasSalvage,
              result.valuation.registrationDate
            );
          } else {
            this.vehicleDetails = new VehicleDetails(null, null, null, null, null, 0, 0, null, null, false, null);
          }
          this.mileage = parseInt(result.answers.find(item => item.parameterName === 'Mileage').value, 10);
          const ancestryCat = result.answers.find(item => item.parameterName === 'AncestryCategory');
          if (ancestryCat) {
            this.ancestryCategory = ancestryCat.value;
          }
          const mileageDecisionAnswer = result.answers.find(item => item.parameterName === 'MileageDecision');
          if (mileageDecisionAnswer) {
            this.mileageDecision = mileageDecisionAnswer.value;
          } else {
            // This is a hack until we re-order saving of answers/check auth
            if (this.authTask.authorizationTypeId === AuthorizationTypes.HistoryClearOverride) {
              this.mileageDecision = '1';
            } else if (this.authTask.authorizationTypeId === AuthorizationTypes.DvsaClericalErrorOverride) {
              this.mileageDecision = '205';
            }
          }

          this.hpiVehicleDetails = new VehicleDetails(
            result.previousMake,
            result.previousColour,
            result.previousModel,
            result.previousDerivative,
            result.previousEngineSize,
            result.previousBookId,
            result.previousCapId,
            result.previousImageUrl,
            result.previousFuelType,
            result.valuation.hasSalvage,
            result.previousRegistrationDate
          );
          this.flexAuth = new FlexAuth(result.vehiclePriceOfferedBeforeFlex - result.valuation.adminFee,
            result.valuation.customerPaymentAmount,
            result.valuation.totalDamageCost,
            result.valuation.totalFlexAmount);
          if (this.isInsuranceWriteOff) {
            const descs = this.pathFinder.getInsuranceWriteOffDescription(result.suggestedWriteOffOption, result.chosenWriteOffOption);
            this.suggestedWriteOffDesc = descs[0];
            this.chosenWriteOffDesc = descs[1];
          }
          this.loading = false;
          this.getHpiDateAnswer();
          this.getMechGradeAnswer();
        }),
        catchError((err) => {
          this.notifications.dangerToast(
            `Failed to retrieve purchase for appointmentId: ${this.appointmentId}`,
            err
          );
          return EMPTY;
        })
      );

    this.declineSubscription = this.eventsService.declineTask.subscribe((response: AuthTask) => {
      this.declineAuthTask(response);
    });
  }


  get isHpiMismatch() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.HpiMismatch;
  }

  get isVehicleDetailsChanged() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.VehicleDetailsChanged;
  }

  get isInsuranceWriteOff() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.InsuranceWriteOffOverride;
  }

  get isOptionalExtra() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.OptionalExtra;
  }

  get isAdditionalEngineCheck() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.AdditionalEngineChecks ||
    this.authTask.authorizationTypeId === AuthorizationTypes.RegionalManagerAdditionalEngineChecks;
  }

  get isFlex() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.FlexBand1 ||
      this.authTask.authorizationTypeId === AuthorizationTypes.FlexBand2 ||
      this.authTask.authorizationTypeId === AuthorizationTypes.SuperFlex;
  }

  get isChangeVrm() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.ChangeVRM;
  }

  get isAltVrm() {
    return this.authTask.authorizationTypeId === AuthorizationTypes.AltVRM;
  }

  get isCapCodeMismatch() {
    return this.authTask.authorizationTypeId == AuthorizationTypes.CapCodeMismatch;
  }

  get isMOTDateChange() {
    return this.authTask.authorizationTypeId == AuthorizationTypes.MOTDateOverride;
  }

  get authTypeWarningMessage() {
    switch (this.authTask.authorizationTypeId) {
      case AuthorizationTypes.HpiMismatch:
        return 'You are approving the HPI vehicle details to be overridden to the customer selected vehicle';
      case AuthorizationTypes.VehicleDetailsChanged:
        return 'This is a Vehicle Details Change Authorisation';
      case AuthorizationTypes.InsuranceWriteOffOverride:
        return 'You are approving the HPI insurance write off marker to be overridden to the Branch Managers selection';
      case AuthorizationTypes.FlexBand1:
        return 'Flex Band 1 authorisation';
      case AuthorizationTypes.FlexBand2:
        return 'Flex Band 2 authorisation';
      case AuthorizationTypes.SuperFlex:
        return 'Super Flex authorisation';
      case AuthorizationTypes.ChangeVRM:
        return 'Change VRM authorisation';
      case AuthorizationTypes.OptionalExtra:
        return 'You are authorising this car to be purchased with these optional extras';
      case AuthorizationTypes.AltVRM:
        return `This is a Alt VRM Authorisation VRM used for HPI Details = ${this.vrmHpiDetails}`;
      case AuthorizationTypes.CapCodeMismatch:
        return `This is a CAP Code Authorisation`;
      case AuthorizationTypes.AdditionalEngineChecks:
        return `This is an Engine Check Authorisation`;
      case AuthorizationTypes.MOTDateOverride:
        return `This is an MOT Date Override Authorisation`;
      case AuthorizationTypes.RegionalManagerAdditionalEngineChecks:
        return `This is a Regional Manager Engine Check Authorisation`;
      default:
        return '';
    }
  }


  getHpiDateAnswer() {
    this.HPIFirstRegistered = this.pathFinder.getAnswerValue('HPIFirstRegisteredDate', null);
  }

  getOptionDescription(value: string): string {
    const parts = value.split(' - ');
    if (parts.length > 1) {
      return parts[1].replace(', ', ',<br>');
    } else {
      return parts[0].replace(', ', ',<br>');
    }
  }

  getOptionIcon(value: string): string {
    const parts = value.split(' ');
    const first = parts[0].replace('/', '-');
    return '../../assets/icons/write-off/write-off-' + first + '.png';
  }

  private setHasAuthDetailsTab() {
    switch (this.authTask.authorizationTypeId) {
      case AuthorizationTypes.HpiMismatch:
      case AuthorizationTypes.VehicleDetailsChanged:
      case AuthorizationTypes.InsuranceWriteOffOverride:
      case AuthorizationTypes.FlexBand1:
      case AuthorizationTypes.FlexBand2:
      case AuthorizationTypes.SuperFlex:
      case AuthorizationTypes.ChangeVRM:
      case AuthorizationTypes.OptionalExtra:
      case AuthorizationTypes.ChangeOfVehicle:
      case AuthorizationTypes.AltVRM:
      case AuthorizationTypes.CapCodeMismatch:
      case AuthorizationTypes.AdditionalEngineChecks:
      case AuthorizationTypes.RegionalManagerAdditionalEngineChecks:
      case AuthorizationTypes.MOTDateOverride:
        this.hasAuthDetailsTab = true;
        break;
      default:
        this.hasAuthDetailsTab = false;
        break;
    }
  }

  approveAuthTask(task: AuthTask) {
    if (this.isAdditionalEngineCheck) {
      const mechGradeModal = this.modalService.open(MechGradeOverrideComponent, {
        keyboard: true,
        backdrop: 'static',
        centered: true,
        size: 'lg'
      });
      mechGradeModal.componentInstance.buyerMechGrade = this.buyerMechGrade;
      mechGradeModal.result.then(
        (mechGradeAuth: MechGradeAuth) => {
          this.actualMechGrade = mechGradeAuth.mechGrade;
          task.declinedReason = mechGradeAuth.notes;
          this.actuallyApproveAuthTask(task);
          this.closeModal();
        },
        () => { }
      );
    } else {
      this.confirmService.showYesNoConfirmation(this.ApproveText,
        () => {
          this.actuallyApproveAuthTask(task);
          this.closeModal();
        }, () => {
      });
    }
  }

  showDeclineModal(task: AuthTask) {
    const declineReasonModal = this.modalService.open(DeclineReasonComponent, {
      keyboard: true,
      backdrop: 'static',
      centered: true,
      size: 'lg'
    });

    declineReasonModal.componentInstance.authTask = task;
  }

  declineAuthTask(task: AuthTask) {
    this.confirmService.showYesNoConfirmation(this.DeclineText,
      () => {
        this.actuallyDeclineAuthTask(task);
        this.closeModal();
      }, () => {
      });
  }

  actuallyApproveAuthTask(task: AuthTask) {
    task.approved = new Date();
    task.authorizedBy = this.buyerName;
    this.authRequestService.updateAuthTaskWithOverriddenAnswers(task, this.getOverriddenAnswers());
  }

  actuallyDeclineAuthTask(task: AuthTask) {
    this.eventsService.closeModal.emit();
    task.declined = new Date();
    task.authorizedBy = this.buyerName;
    this.authRequestService.updateAuthTask(task);
  }

  dismissModal() {
    this.declineSubscription.unsubscribe();
    this.modal.dismiss();
  }

  closeModal() {
    this.declineSubscription.unsubscribe();
    this.modal.close();
  }

  checkPrivatePlate(privatePlate) {
    if (privatePlate.toLowerCase() === 'false') {
      return false;
    } else {
      return true;
    }
  }

  isPdf(file: string) {
    return file && file.toLowerCase().endsWith('.pdf');
  }

  showPreview(photo, stamp, content) {
    if (photo.vehicleImageId) {
      this.imageService
        .getImagePreview$(photo.vehicleImageId, ImageTypes.VehiclePhoto)
        .pipe(
          catchError((err) => {
            this.notifications.dangerToast(
              `An error occurred while trying to get existing images for vehicleImageId ${photo.vehicleImageId}.`,
              err
            );
            return EMPTY;
          }),
          takeUntil(this.componentDestroyed)
        )
        .subscribe((result) => {
          this.previewString =
            this.sanitizer.bypassSecurityTrustUrl(
              result.imgAsBase64String
            );
          this.previewStamp = result.description;
          this.modalService.open(content, { size: 'lg' });
        });
    } else if (photo.documentScanId) {
      this.imageService
        .getImagePreview$(photo.documentScanId, ImageTypes.ScannedDocument)
        .pipe(
          catchError((err) => {
            this.notifications.dangerToast(
              `An error occurred while trying to get existing images for documentScanId ${photo.documentScanId}.`,
              err
            );
            return EMPTY;
          }),
          takeUntil(this.componentDestroyed)
        )
        .subscribe((result) => {
          this.previewString = this.sanitizer.bypassSecurityTrustUrl(
            result.imgAsBase64String
          );
          this.previewStamp = result.documentName;
          this.modalService.open(content, { size: 'lg' });
        });
    } else {
      this.previewString = photo.imgAsBase64String;
      this.previewStamp = stamp;
      this.modalService.open(content, { size: 'lg' });
    }
  }

  editVehicleImages() {
    this.router.navigate(['/postPurchase/images', this.quoteStateId]);
  }

  show(answer) {
    const modalRef = this.modalService.open(DisplayAnswerComponent, {
      keyboard: false,
      centered: true,
      size: 'lg',
    });
    modalRef.componentInstance.displayValue = answer.displayValue;
    modalRef.componentInstance.parameterName = answer.parameterName;
  }

  setActiveTab(tabIndex: number) {
    this.activeTab = tabIndex;
  }

  getMechGradeAnswer() {
    const mechGradeAnswer = this.allAnswers.find(item => item.parameterName === 'Mechanical Grade Assessment');
    if (mechGradeAnswer) {
      this.buyerMechGrade = mechGradeAnswer.value;
      this.actualMechGrade = this.buyerMechGrade;
    } else {
      const jlrMechGradeAnswer = this.allAnswers.find(item => item.parameterName === 'JLR Mechanical Grade Assessment');
      if (jlrMechGradeAnswer) {
        this.buyerMechGrade = jlrMechGradeAnswer.value;
        this.actualMechGrade = this.buyerMechGrade;
      }
    }
  }

  getOverriddenAnswers(): Array<AnswerValue> {
    if (this.isAdditionalEngineCheck && this.actualMechGrade !== this.buyerMechGrade) {
      const answers = new Array<AnswerValue>();
      const jlrMechGradeAnswer = this.allAnswers.find(item => item.parameterName === 'JLR Mechanical Grade Assessment');
      if (jlrMechGradeAnswer) {
        answers.push(new AnswerValue('JLR Mechanical Grade Assessment', this.actualMechGrade, null, null, false, false));
      } else {
        answers.push(new AnswerValue('Mechanical Grade Assessment', this.actualMechGrade, null, null, false, false));
      }
      answers.push(new AnswerValue('Mechanic Override Grade Assessment', this.actualMechGrade, null, null, false, false));
      answers.push(new AnswerValue('AuthProceed', 'false', `Approved with Mech Grade ${this.actualMechGrade.substring(this.actualMechGrade.length - 1)}`, null, false, false));
      return answers;
    } else {
      return null;
    }
  }

  isMechGradeEnabled(grade: string): boolean {
    if (this.buyerMechGrade === 'GA') {
      return true;
    } else if (this.buyerMechGrade === 'GB') {
      return grade !== 'GA';
    } else {
      return grade === 'GC';
    }
  }
}
